import {
  AuthState,
  CustomUserClaims,
  OktaAuth,
  SigninWithCredentialsOptions,
  Tokens,
  UserClaims,
} from "@okta/okta-auth-js";
import { OktaContext } from "@okta/okta-react";
import { ReactNode, useMemo, useRef, useState } from "react";

import { demoAccount } from "../container/apollo-client/demo-data";
import { DEMO_USER_EMAIL, DEMO_USER_PASSWORD } from "../lib/env-helpers";
import { UserRoles } from "../lib/role-based-access-control";
import { AccountType, RegistryCode } from "../types/generated/graphql";

const createDemoAccessToken = () => ({
  accessToken: "demo-access-token",
  tokenType: "Bearer",
  expiresAt: Math.floor(Date.now() / 1000) + 60 * 60,
  scopes: ["openid", "email"],
  authorizeUrl: "https://demo.powerledger.io/login",
  userinfoUrl: "https://demo.powerledger.io/userinfo",
  issuer: "https://demo.powerledger.io",
  clientId: "demo-client",
  claims: {
    sub: demoAccount.email,
    email: demoAccount.email,
    roles: [UserRoles.RoleAdmin],
    forwardTradeEnabled: true,
    given_name: demoAccount.firstName,
    family_name: demoAccount.lastName,
    accountUid: demoAccount.id,
    enabledRegistries: JSON.stringify({
      [RegistryCode.Mrets]: {
        accountType: AccountType.Registry,
      },
      [RegistryCode.Ercot]: {
        accountType: AccountType.NonRegistry,
      },
    }),
  } as UserClaims<CustomUserClaims>,
});

const createDemoAuthState = (): AuthState => ({
  isAuthenticated: false,
  isPending: false,
  accessToken: undefined,
});

type Listener = (authState: AuthState | null) => void;

const createDemoOktaAuth = (getAuthState: () => AuthState | null, notify: (authState: AuthState | null) => void) => {
  const listeners = new Set<Listener>();

  const demoAccessToken = createDemoAccessToken();

  const subscribe = (listener: Listener) => {
    listeners.add(listener);
  };

  const unsubscribe = (listener: Listener) => {
    listeners.delete(listener);
  };

  const emit = (authState: AuthState | null) => {
    listeners.forEach((listener) => listener(authState));
    notify(authState);
  };

  return {
    authStateManager: {
      getAuthState,
      subscribe,
      unsubscribe,
      updateAuthState: async () => {
        emit(getAuthState());
      },
    },
    tokenManager: {
      getTokensSync: () => ({ accessToken: demoAccessToken }),
      getTokens: async () => ({ accessToken: demoAccessToken } as Tokens),
      setTokens: () => {},
      renew: async () => demoAccessToken,
      clearPendingRemoveTokens: () => {},
      on: () => {},
      off: () => {},
    },
    token: {
      getWithRedirect: async () => {},
      getWithoutPrompt: async () => ({ tokens: { accessToken: demoAccessToken } }),
    },
    signOut: async ({ postLogoutRedirectUri }: { postLogoutRedirectUri?: string } = {}) => {
      emit({
        isAuthenticated: false,
        isPending: false,
      } as AuthState);
      if (postLogoutRedirectUri) {
        window.location.assign(postLogoutRedirectUri);
      }
    },
    signInWithCredentials: async (credentials: SigninWithCredentialsOptions) => {
      if (credentials.username !== DEMO_USER_EMAIL || credentials.password !== DEMO_USER_PASSWORD) {
        return { status: "ERROR", error: "Invalid credentials" };
      }

      const nextAuthState = {
        isAuthenticated: true,
        isPending: false,
        accessToken: demoAccessToken as AuthState["accessToken"],
      } satisfies AuthState;

      emit(nextAuthState);
      return { status: "SUCCESS", sessionToken: "demo-session-token", user: { id: "demo-user" } };
    },
    forgotPassword: async () => {},
    idx: {
      canProceed: () => false,
    },
    options: {},
  } as unknown as OktaAuth;
};

export const DemoOktaProvider = ({ children }: { children: ReactNode }) => {
  const [, forceRender] = useState(0);
  const authStateRef = useRef<AuthState | null>(createDemoAuthState());

  const notify = (authState: AuthState | null) => {
    authStateRef.current = authState;
    forceRender((current) => current + 1);
  };

  const oktaAuth = useMemo(
    () => createDemoOktaAuth(() => authStateRef.current, notify),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const authState = authStateRef.current;

  return (
    <OktaContext.Provider value={{ oktaAuth, authState, _onAuthRequired: () => {} }}>{children}</OktaContext.Provider>
  );
};
